import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, FileText, RefreshCw, AlertCircle, Save, Loader2, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from "react-markdown";

interface DocumentSummaryProps {
  attachmentId: string;
  claimId: string;
  fileName: string;
  fileType: string;
  extractedText: string | null;
  onGetSignedUrl: () => Promise<string | null>;
}

export const DocumentSummary = ({
  attachmentId,
  claimId,
  fileName,
  fileType,
  extractedText,
  onGetSignedUrl,
}: DocumentSummaryProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const { toast } = useToast();

  const handleSummarize = useCallback(async () => {
    setIsLoading(true);
    setSummary(null);
    setHasError(false);
    setIsSaved(false);

    try {
      let requestBody: Record<string, any> = {
        attachmentId,
      };

      // If we have extracted text, use it
      if (extractedText) {
        requestBody.extractedText = extractedText;
      } else {
        // Otherwise, get signed URL for vision analysis
        const signedUrl = await onGetSignedUrl();
        if (!signedUrl) {
          throw new Error("Failed to get file URL");
        }
        requestBody.fileUrl = signedUrl;
        requestBody.fileType = fileType;
      }

      const { data, error } = await supabase.functions.invoke("summarize-document", {
        body: requestBody,
      });

      if (error) {
        // Check for specific error status codes
        if (error.message?.includes("429") || error.message?.includes("rate limit")) {
          toast({
            title: "Rate Limit Exceeded",
            description: "Too many requests. Please wait a moment and try again.",
            variant: "destructive",
          });
          throw new Error("Rate limit exceeded");
        }
        
        if (error.message?.includes("402") || error.message?.includes("payment")) {
          toast({
            title: "Credits Required",
            description: "AI credits have been exhausted. Please add more credits to continue.",
            variant: "destructive",
          });
          throw new Error("Payment required");
        }
        
        throw error;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setSummary(data.summary);
      toast({
        title: "Summary Complete",
        description: "Document has been analyzed successfully.",
      });
    } catch (error: any) {
      console.error("Error summarizing document:", error);
      setHasError(true);
      
      // Only show generic toast for errors not already handled
      if (error.message !== "Rate limit exceeded" && error.message !== "Payment required") {
        toast({
          title: "Summarization Failed",
          description: error.message || "Failed to summarize document. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [attachmentId, extractedText, fileType, onGetSignedUrl, toast]);

  const handleSaveToNotes = useCallback(async () => {
    if (!summary || !claimId) return;

    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("You must be logged in to save notes");
      }

      const noteContent = `## AI Summary: ${fileName}\n\n${summary}\n\n---\n*Generated on ${new Date().toLocaleString()}*`;

      const { error } = await supabase
        .from("claim_notes")
        .insert({
          claim_id: claimId,
          user_id: user.id,
          content: noteContent,
        });

      if (error) throw error;

      setIsSaved(true);
      toast({
        title: "Saved to Notes",
        description: "The AI summary has been saved to claim notes.",
      });
    } catch (error: any) {
      console.error("Error saving to notes:", error);
      toast({
        title: "Failed to Save",
        description: error.message || "Could not save summary to notes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }, [summary, claimId, fileName, toast]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && !summary && !isLoading && !hasError) {
      handleSummarize();
    }
    if (!open) {
      // Reset saved state when closing
      setIsSaved(false);
    }
  };

  const LoadingSkeleton = () => (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
      <p className="text-sm text-muted-foreground text-center pt-2">Analyzing document with AI...</p>
    </div>
  );

  const ErrorState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="h-12 w-12 text-destructive mb-4" />
      <h3 className="font-semibold mb-2">Failed to summarize document</h3>
      <p className="text-sm text-muted-foreground mb-6">
        Something went wrong while analyzing the document. Please try again.
      </p>
      <Button onClick={handleSummarize} variant="outline">
        <RefreshCw className="h-4 w-4 mr-2" />
        Retry
      </Button>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" title="AI Summary">
          <Sparkles className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Document Summary
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex items-center gap-2 text-sm text-muted-foreground border-b pb-3">
          <FileText className="h-4 w-4" />
          <span className="truncate">{fileName}</span>
        </div>

        <ScrollArea className="max-h-[60vh] pr-4">
          {isLoading ? (
            <LoadingSkeleton />
          ) : hasError && !summary ? (
            <ErrorState />
          ) : summary ? (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown>{summary}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>Click the button below to generate a summary</p>
            </div>
          )}
        </ScrollArea>

        {!isLoading && !hasError && summary && (
          <div className="flex justify-between gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={handleSaveToNotes}
              disabled={isSaving || isSaved}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : isSaved ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Saved to Notes
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save to Notes
                </>
              )}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Close
              </Button>
              <Button onClick={handleSummarize} disabled={isLoading}>
                <Sparkles className="h-4 w-4 mr-2" />
                Regenerate
              </Button>
            </div>
          </div>
        )}

        {!isLoading && !hasError && !summary && (
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Close
            </Button>
            <Button onClick={handleSummarize} disabled={isLoading}>
              <Sparkles className="h-4 w-4 mr-2" />
              Summarize
            </Button>
          </div>
        )}
        
        {hasError && !summary && (
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
